import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import {
  Plane, Plus, Search, Calendar, Clock, MapPin, Globe, Home,
  ChevronLeft, ChevronRight, AlertTriangle, Users, Filter, History, ArrowRight,
  Pencil, Trash2, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { formatDateForInput, formatDisplayDate } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRPORTS = [
  { code: 'PER', name: 'Perth' }, { code: 'PHE', name: 'Port Hedland' }, { code: 'KTA', name: 'Karratha' },
  { code: 'BME', name: 'Broome' }, { code: 'NTL', name: 'Newman' }, { code: 'LEA', name: 'Learmonth' },
  { code: 'DRW', name: 'Darwin' }, { code: 'CNS', name: 'Cairns' }, { code: 'BNE', name: 'Brisbane' },
  { code: 'SYD', name: 'Sydney' }, { code: 'MEL', name: 'Melbourne' }, { code: 'ADL', name: 'Adelaide' },
  { code: 'SIN', name: 'Singapore' }, { code: 'KUL', name: 'Kuala Lumpur' }, { code: 'JKT', name: 'Jakarta' },
  { code: 'MNL', name: 'Manila' }, { code: 'BKK', name: 'Bangkok' }, { code: 'DXB', name: 'Dubai' },
  { code: 'DOH', name: 'Doha' }, { code: 'AKL', name: 'Auckland' }, { code: 'POM', name: 'Port Moresby' },
];

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function MOBTracker({ user }) {
  const notification = useNotification();
  const { canEdit: canEditModule } = usePermissions();
  const canEdit = canEditModule('mob_tracker');

  const [activeTab, setActiveTab] = useState('upcoming');
  const [flights, setFlights] = useState([]);
  const [plannerSync, setPlannerSync] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Quick FIFO input
  const [fifoSheetOpen, setFifoSheetOpen] = useState(false);
  const [editingFifoId, setEditingFifoId] = useState(null); // when set, the sheet is in edit mode
  const [fifoForm, setFifoForm] = useState({
    personnel_id: '', flight_type: 'Domestic', status: 'Planned',
    fly_in_date: '', fly_in_time: '', fly_in_flight: '', fly_in_from: '', fly_in_to: '',
    fly_out_date: '', fly_out_time: '', fly_out_flight: '', fly_out_from: '', fly_out_to: '',
    project: '', location: '', notes: ''
  });

  // Timeline state
  const [timelineDate, setTimelineDate] = useState(new Date());

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [flightsRes, syncRes, personnelRes, projectsRes] = await Promise.all([
        axios.get(`${API}/mob-tracker/flights`, { headers: getAuthHeader() }),
        axios.get(`${API}/mob-tracker/planner-sync`, { headers: getAuthHeader() }),
        axios.get(`${API}/personnel`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
      ]);
      setFlights(flightsRes.data);
      setPlannerSync(syncRes.data);
      setPersonnel(personnelRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      notification.error('Failed to load MOB tracker data');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Split flights into upcoming and history
  const filteredFlights = useMemo(() => {
    return flights.filter(f => {
      const matchesSearch = !searchFilter ||
        f.personnel_name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        f.project?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        f.fly_in_flight?.toLowerCase().includes(searchFilter.toLowerCase());
      const matchesType = typeFilter === 'all' || f.flight_type === typeFilter;
      const matchesStatus = statusFilter === 'all' || f.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [flights, searchFilter, typeFilter, statusFilter]);

  const upcomingFlights = useMemo(() =>
    filteredFlights.filter(f => f.fly_in_date >= today || f.fly_out_date >= today)
      .sort((a, b) => new Date(a.fly_in_date) - new Date(b.fly_in_date)),
    [filteredFlights, today]);

  const pastFlights = useMemo(() =>
    filteredFlights.filter(f => f.fly_in_date < today && f.fly_out_date < today)
      .sort((a, b) => new Date(b.fly_in_date) - new Date(a.fly_in_date)),
    [filteredFlights, today]);

  // Timeline: 4 weeks from timelineDate
  const timelineWeeks = useMemo(() => {
    const weeks = [];
    const start = new Date(timelineDate);
    start.setDate(start.getDate() - start.getDay()); // Start from Sunday
    for (let i = 0; i < 28; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      weeks.push(d);
    }
    return weeks;
  }, [timelineDate]);

  // Group upcoming flights by person for timeline
  const timelinePersonnel = useMemo(() => {
    const map = {};
    upcomingFlights.forEach(f => {
      if (!map[f.personnel_id]) {
        map[f.personnel_id] = { name: f.personnel_name, role: f.personnel_role, flights: [] };
      }
      map[f.personnel_id].flights.push(f);
    });
    return Object.entries(map).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [upcomingFlights]);

  // Planner allocations grouped by person
  const syncByPerson = useMemo(() => {
    const map = {};
    plannerSync.forEach(a => {
      const person = personnel.find(p => p.id === a.personnel_id);
      if (!person) return;
      if (!map[a.personnel_id]) {
        map[a.personnel_id] = { name: person.name, role: person.role, allocations: [], flights: [] };
      }
      map[a.personnel_id].allocations.push(a);
    });
    // Add flights
    flights.forEach(f => {
      if (map[f.personnel_id]) {
        map[f.personnel_id].flights.push(f);
      }
    });
    return Object.entries(map).filter(([, v]) => v.allocations.length > 0 || v.flights.length > 0)
      .sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [plannerSync, personnel, flights]);

  // Quick FIFO add
  const handleQuickFifo = async (e) => {
    e.preventDefault();
    if (!fifoForm.personnel_id || !fifoForm.fly_in_date || !fifoForm.fly_out_date) {
      notification.error('Personnel, Fly In, and Fly Out dates are required');
      return;
    }
    try {
      // Get existing personnel data, add or update FIFO record, and save
      const personRes = await axios.get(`${API}/personnel/${fifoForm.personnel_id}`, { headers: getAuthHeader() });
      const person = personRes.data;
      const existingFifos = (person.fifo_dates || []).map(f => ({
        ...f,
        fly_in_date: f.fly_in_date ? new Date(f.fly_in_date).toISOString() : null,
        fly_out_date: f.fly_out_date ? new Date(f.fly_out_date).toISOString() : null,
      }));
      const newFifo = {
        flight_type: fifoForm.flight_type,
        fly_in_date: new Date(fifoForm.fly_in_date).toISOString(),
        fly_in_time: fifoForm.fly_in_time || null,
        fly_in_flight: fifoForm.fly_in_flight || null,
        fly_in_from: fifoForm.fly_in_from || null,
        fly_in_to: fifoForm.fly_in_to || null,
        fly_out_date: new Date(fifoForm.fly_out_date).toISOString(),
        fly_out_time: fifoForm.fly_out_time || null,
        fly_out_flight: fifoForm.fly_out_flight || null,
        fly_out_from: fifoForm.fly_out_from || null,
        fly_out_to: fifoForm.fly_out_to || null,
        project: fifoForm.project || null,
        location: fifoForm.location || null,
        status: fifoForm.status,
        notes: fifoForm.notes || null,
      };
      let updatedFifos;
      if (editingFifoId) {
        // Replace the matching record (preserve its id)
        updatedFifos = existingFifos.map(f => f.id === editingFifoId ? { ...f, ...newFifo } : f);
      } else {
        updatedFifos = [...existingFifos, newFifo];
      }

      await axios.put(`${API}/mob-tracker/personnel/${fifoForm.personnel_id}/fifo`, {
        fifo_dates: updatedFifos,
        version: person.version,
      }, { headers: getAuthHeader() });

      notification.success(editingFifoId ? 'Flight updated successfully' : 'FIFO record added successfully');
      setFifoSheetOpen(false);
      setEditingFifoId(null);
      setFifoForm({ personnel_id: '', flight_type: 'Domestic', status: 'Planned', fly_in_date: '', fly_in_time: '', fly_in_flight: '', fly_in_from: '', fly_in_to: '', fly_out_date: '', fly_out_time: '', fly_out_flight: '', fly_out_from: '', fly_out_to: '', project: '', location: '', notes: '' });
      fetchAll();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to save FIFO record');
    }
  };

  // Open Sheet pre-filled for editing an existing flight
  const openEditFifo = (flight) => {
    setEditingFifoId(flight.fifo_id);
    setFifoForm({
      personnel_id: flight.personnel_id,
      flight_type: flight.flight_type || 'Domestic',
      status: flight.status || 'Planned',
      fly_in_date: flight.fly_in_date ? formatDateForInput(flight.fly_in_date) : '',
      fly_in_time: flight.fly_in_time || '',
      fly_in_flight: flight.fly_in_flight || '',
      fly_in_from: flight.fly_in_from || '',
      fly_in_to: flight.fly_in_to || '',
      fly_out_date: flight.fly_out_date ? formatDateForInput(flight.fly_out_date) : '',
      fly_out_time: flight.fly_out_time || '',
      fly_out_flight: flight.fly_out_flight || '',
      fly_out_from: flight.fly_out_from || '',
      fly_out_to: flight.fly_out_to || '',
      project: flight.project || '',
      location: flight.location || '',
      notes: flight.notes || '',
    });
    setFifoSheetOpen(true);
  };

  const handleDeleteFifo = (flight) => {
    notification.confirm(
      `Delete this flight for ${flight.personnel_name}?\n\n${flight.fly_in_from || '?'} → ${flight.fly_in_to || '?'} on ${formatDisplayDate(flight.fly_in_date)}.\n\nThis cannot be undone.`,
      async () => {
        try {
          const personRes = await axios.get(`${API}/personnel/${flight.personnel_id}`, { headers: getAuthHeader() });
          const person = personRes.data;
          const updatedFifos = (person.fifo_dates || [])
            .filter(f => f.id !== flight.fifo_id)
            .map(f => ({
              ...f,
              fly_in_date: f.fly_in_date ? new Date(f.fly_in_date).toISOString() : null,
              fly_out_date: f.fly_out_date ? new Date(f.fly_out_date).toISOString() : null,
            }));
          await axios.put(`${API}/mob-tracker/personnel/${flight.personnel_id}/fifo`, {
            fifo_dates: updatedFifos,
            version: person.version,
          }, { headers: getAuthHeader() });
          notification.success('Flight deleted');
          fetchAll();
        } catch (error) {
          notification.error(error.response?.data?.detail || 'Failed to delete flight');
        }
      },
      { title: 'Delete Flight', confirmLabel: 'Delete', confirmClass: 'bg-red-600 hover:bg-red-700' }
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Planned': 'bg-blue-50 text-blue-700 border-blue-200',
      'Confirmed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Completed': 'bg-slate-100 text-slate-600 border-slate-200',
      'Cancelled': 'bg-red-50 text-red-700 border-red-200',
    };
    return variants[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const navigateTimeline = (dir) => {
    const d = new Date(timelineDate);
    d.setDate(d.getDate() + dir * 7);
    setTimelineDate(d);
  };

  // Flight row component used in both tabs
  const FlightRow = ({ f }) => (
    <TableRow key={f.fifo_id} data-testid={`flight-row-${f.fifo_id}`}>
      <TableCell className="font-medium text-sm">
        <div>{f.personnel_name}</div>
        <div className="text-xs text-slate-500">{f.personnel_role}</div>
      </TableCell>
      <TableCell>
        <Badge className={`text-xs ${f.flight_type === 'International' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
          {f.flight_type === 'International' ? <Globe className="w-3 h-3 mr-1 inline" /> : <Home className="w-3 h-3 mr-1 inline" />}
          {f.flight_type}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        <div className="flex items-center gap-1">
          <Plane className="w-3 h-3 text-emerald-600 -rotate-45" />
          <span>{formatDisplayDate(f.fly_in_date)}</span>
          {f.fly_in_time && <span className="text-xs text-slate-400">@{f.fly_in_time}</span>}
        </div>
        {(f.fly_in_from || f.fly_in_to) && (
          <div className="text-xs text-slate-600 ml-4 flex items-center gap-1" data-testid={`flight-route-in-${f.fifo_id}`}>
            <span>{f.fly_in_from || '?'}</span>
            <ArrowRight className="w-2.5 h-2.5 text-slate-400" />
            <span>{f.fly_in_to || '?'}</span>
          </div>
        )}
        {f.fly_in_flight && <div className="text-xs text-slate-400 ml-4">{f.fly_in_flight}</div>}
      </TableCell>
      <TableCell className="text-sm">
        <div className="flex items-center gap-1">
          <Plane className="w-3 h-3 text-blue-600 rotate-45" />
          <span>{formatDisplayDate(f.fly_out_date)}</span>
          {f.fly_out_time && <span className="text-xs text-slate-400">@{f.fly_out_time}</span>}
        </div>
        {(f.fly_out_from || f.fly_out_to) && (
          <div className="text-xs text-slate-600 ml-4 flex items-center gap-1" data-testid={`flight-route-out-${f.fifo_id}`}>
            <span>{f.fly_out_from || '?'}</span>
            <ArrowRight className="w-2.5 h-2.5 text-slate-400" />
            <span>{f.fly_out_to || '?'}</span>
          </div>
        )}
        {f.fly_out_flight && <div className="text-xs text-slate-400 ml-4">{f.fly_out_flight}</div>}
      </TableCell>
      <TableCell className="text-sm">{f.project || '-'}</TableCell>
      <TableCell><Badge className={getStatusBadge(f.status)}>{f.status}</Badge></TableCell>
      {canEdit && (
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0" data-testid={`flight-actions-${f.fifo_id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditFifo(f)} data-testid={`flight-edit-${f.fifo_id}`}>
                <Pencil className="mr-2 h-4 w-4" />Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteFifo(f)} data-testid={`flight-delete-${f.fifo_id}`} className="text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="mob-tracker-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">MOB Tracker</h1>
          <p className="text-sm text-slate-600 mt-2">Mobilization, flight tracking & planner synchronization</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 rounded-lg px-3 py-1.5">
            <Plane className="w-4 h-4 text-amber-500" />
            <span className="font-medium">{upcomingFlights.length}</span>
            <span>upcoming</span>
            <span className="text-slate-300 mx-1">|</span>
            <History className="w-4 h-4 text-slate-400" />
            <span className="font-medium">{pastFlights.length}</span>
            <span>past</span>
          </div>
          {canEdit && (
            <Button
              onClick={() => setFifoSheetOpen(true)}
              data-testid="quick-add-fifo-button"
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm rounded-md px-6 py-2 font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Quick Add Flight
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search name, project, flight..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="pl-9 h-9"
            data-testid="mob-search"
          />
        </div>
        <div className="flex border border-slate-200 rounded-md overflow-hidden">
          {['all', 'Domestic', 'International'].map(t => (
            <button key={t} type="button" onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 text-xs font-medium ${typeFilter === t ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid={`type-filter-${t}`}
            >{t === 'all' ? 'All Types' : t}</button>
          ))}
        </div>
        <div className="flex border border-slate-200 rounded-md overflow-hidden">
          {['all', 'Planned', 'Confirmed', 'Completed', 'Cancelled'].map(s => (
            <button key={s} type="button" onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium ${statusFilter === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid={`status-filter-${s}`}
            >{s === 'all' ? 'All Status' : s}</button>
          ))}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="upcoming" data-testid="upcoming-tab">
            <Plane className="w-4 h-4 mr-1.5" />Upcoming Flights ({upcomingFlights.length})
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="timeline-tab">
            <Calendar className="w-4 h-4 mr-1.5" />Timeline View
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <History className="w-4 h-4 mr-1.5" />Flight History ({pastFlights.length})
          </TabsTrigger>
          <TabsTrigger value="sync" data-testid="sync-tab">
            <Users className="w-4 h-4 mr-1.5" />Planner Sync
          </TabsTrigger>
        </TabsList>

        {/* UPCOMING FLIGHTS TAB */}
        <TabsContent value="upcoming">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">Upcoming Flights</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingFlights.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Plane className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No upcoming flights</p>
                  <p className="text-xs mt-1">Use "Quick Add Flight" to schedule FIFO movements</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">Personnel</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Type</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Fly In</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Fly Out</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Project</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                      {canEdit && <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcomingFlights.map(f => <FlightRow key={f.fifo_id} f={f} />)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TIMELINE VIEW TAB */}
        <TabsContent value="timeline">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">Flight Timeline</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigateTimeline(-1)} className="h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm font-medium text-slate-600">{timelineWeeks[0]?.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })} - {timelineWeeks[timelineWeeks.length - 1]?.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <Button variant="ghost" size="sm" onClick={() => navigateTimeline(1)} className="h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setTimelineDate(new Date())} className="h-7 text-xs">Today</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {timelinePersonnel.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No flights in this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Timeline header */}
                  <div className="flex border-b border-slate-200 min-w-[900px]">
                    <div className="w-48 flex-shrink-0 p-2 text-xs font-semibold text-slate-500 uppercase">Personnel</div>
                    <div className="flex-1 flex">
                      {timelineWeeks.map((d, i) => {
                        const isToday = d.toDateString() === new Date().toDateString();
                        const isSunday = d.getDay() === 0;
                        return (
                          <div key={i} className={`flex-1 text-center text-[10px] p-1 border-l border-slate-100 ${isToday ? 'bg-amber-50 font-bold text-amber-700' : isSunday ? 'bg-slate-50' : ''}`}>
                            <div>{d.toLocaleDateString('en-AU', { weekday: 'narrow' })}</div>
                            <div>{d.getDate()}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Timeline rows */}
                  {timelinePersonnel.map(([personId, data]) => (
                    <div key={personId} className="flex border-b border-slate-100 min-w-[900px] hover:bg-slate-50">
                      <div className="w-48 flex-shrink-0 p-2">
                        <div className="text-sm font-medium text-slate-800 truncate">{data.name}</div>
                        <div className="text-xs text-slate-500">{data.role}</div>
                      </div>
                      <div className="flex-1 flex relative" style={{ minHeight: '40px' }}>
                        {timelineWeeks.map((d, i) => {
                          const isToday = d.toDateString() === new Date().toDateString();
                          const isSunday = d.getDay() === 0;
                          return <div key={i} className={`flex-1 border-l border-slate-100 ${isToday ? 'bg-amber-50/50' : isSunday ? 'bg-slate-50/50' : ''}`} />;
                        })}
                        {/* Flight bars overlay */}
                        {data.flights.map(f => {
                          const tStart = timelineWeeks[0];
                          const tEnd = timelineWeeks[timelineWeeks.length - 1];
                          const fIn = new Date(f.fly_in_date);
                          const fOut = new Date(f.fly_out_date);
                          if (fOut < tStart || fIn > tEnd) return null;
                          const totalDays = (tEnd - tStart) / 86400000;
                          const left = Math.max(0, (fIn - tStart) / 86400000 / totalDays * 100);
                          const width = Math.min(100 - left, Math.max(2, ((fOut - fIn) / 86400000 / totalDays * 100)));
                          const color = f.status === 'Confirmed' ? 'bg-emerald-400' : f.status === 'Planned' ? 'bg-blue-400' : 'bg-slate-300';
                          return (
                            <div
                              key={f.fifo_id}
                              className={`absolute top-2 h-5 ${color} rounded-sm text-[10px] text-white flex items-center justify-center overflow-hidden shadow-sm`}
                              style={{ left: `${left}%`, width: `${width}%` }}
                              title={`${f.personnel_name}: ${formatDisplayDate(f.fly_in_date)} - ${formatDisplayDate(f.fly_out_date)} (${f.status})\n${f.project || ''}`}
                            >
                              <span className="truncate px-1">{f.fly_in_from || ''}{f.fly_in_from && f.fly_in_to ? '→' : ''}{f.fly_in_to || ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FLIGHT HISTORY TAB */}
        <TabsContent value="history">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">Flight History</CardTitle>
            </CardHeader>
            <CardContent>
              {pastFlights.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No past flights</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">Personnel</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Type</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Fly In</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Fly Out</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Project</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                      {canEdit && <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastFlights.map(f => <FlightRow key={f.fifo_id} f={f} />)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANNER SYNC TAB */}
        <TabsContent value="sync">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">Planner Sync</CardTitle>
                <p className="text-xs text-slate-500">Shows Personnel Planner allocations alongside FIFO schedules</p>
              </div>
            </CardHeader>
            <CardContent>
              {syncByPerson.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No planner allocations found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {syncByPerson.map(([personId, data]) => (
                    <Card key={personId} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="font-semibold text-slate-800">{data.name}</span>
                            <span className="text-sm text-slate-500 ml-2">{data.role}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{data.allocations.length} allocation{data.allocations.length !== 1 ? 's' : ''}</Badge>
                            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{data.flights.length} flight{data.flights.length !== 1 ? 's' : ''}</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Allocations */}
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Planner Allocations</div>
                            {data.allocations.length === 0 ? (
                              <p className="text-xs text-slate-400">No allocations</p>
                            ) : (
                              <div className="space-y-1">
                                {data.allocations.map(a => (
                                  <div key={a.allocation_id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs">
                                    <span className="font-medium text-slate-700">{a.project_name}</span>
                                    <span className="text-slate-500">{formatDisplayDate(a.start_date)} - {formatDisplayDate(a.end_date)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Flights */}
                          <div>
                            <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">FIFO Flights</div>
                            {data.flights.length === 0 ? (
                              <p className="text-xs text-slate-400">No flights scheduled</p>
                            ) : (
                              <div className="space-y-1">
                                {data.flights.sort((a, b) => new Date(a.fly_in_date) - new Date(b.fly_in_date)).map(f => (
                                  <div key={f.fifo_id} className="flex flex-col gap-1 p-2 bg-amber-50 rounded text-xs" data-testid={`overview-flight-${f.fifo_id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-1">
                                        <Plane className="w-3 h-3 text-emerald-600 -rotate-45" />
                                        <span className="font-medium text-amber-800 whitespace-nowrap">
                                          {f.fly_in_from || '?'} <ArrowRight className="w-2.5 h-2.5 inline" /> {f.fly_in_to || '?'}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-500 whitespace-nowrap">{formatDisplayDate(f.fly_in_date)}</span>
                                        <Badge className={`text-[10px] ${getStatusBadge(f.status)}`}>{f.status}</Badge>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-1">
                                        <Plane className="w-3 h-3 text-blue-600 rotate-45" />
                                        <span className="font-medium text-amber-800 whitespace-nowrap">
                                          {f.fly_out_from || '?'} <ArrowRight className="w-2.5 h-2.5 inline" /> {f.fly_out_to || '?'}
                                        </span>
                                      </div>
                                      <span className="text-slate-500 whitespace-nowrap">{formatDisplayDate(f.fly_out_date)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Sync warnings */}
                        {data.allocations.length > 0 && data.flights.length === 0 && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            No flights scheduled for active allocation — consider booking travel
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Add / Edit FIFO Sheet */}
      <Sheet open={fifoSheetOpen} onOpenChange={(open) => {
        setFifoSheetOpen(open);
        if (!open) {
          setEditingFifoId(null);
          setFifoForm({ personnel_id: '', flight_type: 'Domestic', status: 'Planned', fly_in_date: '', fly_in_time: '', fly_in_flight: '', fly_in_from: '', fly_in_to: '', fly_out_date: '', fly_out_time: '', fly_out_flight: '', fly_out_from: '', fly_out_to: '', project: '', location: '', notes: '' });
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-xl lg:max-w-3xl overflow-y-auto" data-testid="fifo-quick-sheet">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">{editingFifoId ? 'Edit Flight' : 'Quick Add Flight'}</SheetTitle>
            <SheetDescription>{editingFifoId ? 'Update an existing FIFO record' : 'Add a FIFO record directly to a personnel member'}</SheetDescription>
          </SheetHeader>
          <form id="quick-fifo-form" onSubmit={handleQuickFifo} className="py-4 space-y-4">
            {/* Personnel select */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Personnel *</Label>
              <select value={fifoForm.personnel_id} onChange={(e) => setFifoForm({ ...fifoForm, personnel_id: e.target.value })}
                disabled={!!editingFifoId}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-700 disabled:cursor-not-allowed" required data-testid="fifo-personnel-select">
                <option value="">Select Personnel</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.name} - {p.role}</option>)}
              </select>
            </div>
            {/* Type & Status */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Flight Type</Label>
                <select value={fifoForm.flight_type} onChange={(e) => setFifoForm({ ...fifoForm, flight_type: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="fifo-type-select">
                  <option value="Domestic">Domestic</option>
                  <option value="International">International</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Status</Label>
                <select value={fifoForm.status} onChange={(e) => setFifoForm({ ...fifoForm, status: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="Planned">Planned</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            {/* Fly In */}
            <div className="border rounded-lg p-3 bg-emerald-50/50">
              <div className="flex items-center gap-2 mb-3">
                <Plane className="w-4 h-4 text-emerald-600 -rotate-45" />
                <span className="text-sm font-semibold text-emerald-700">Fly In</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs font-semibold">Date *</Label><Input type="date" value={fifoForm.fly_in_date} onChange={(e) => setFifoForm({ ...fifoForm, fly_in_date: e.target.value })} required data-testid="fifo-fly-in-date" /></div>
                <div><Label className="text-xs font-semibold">Time</Label><Input type="time" value={fifoForm.fly_in_time} onChange={(e) => setFifoForm({ ...fifoForm, fly_in_time: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">Flight #</Label><Input value={fifoForm.fly_in_flight} onChange={(e) => setFifoForm({ ...fifoForm, fly_in_flight: e.target.value.toUpperCase() })} placeholder="QF1234" /></div>
                <div></div>
                <div><Label className="text-xs font-semibold">From</Label><select value={fifoForm.fly_in_from} onChange={(e) => setFifoForm({ ...fifoForm, fly_in_from: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Airport</option>{AIRPORTS.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}</select></div>
                <div><Label className="text-xs font-semibold">To</Label><select value={fifoForm.fly_in_to} onChange={(e) => setFifoForm({ ...fifoForm, fly_in_to: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Airport</option>{AIRPORTS.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}</select></div>
              </div>
            </div>
            {/* Fly Out */}
            <div className="border rounded-lg p-3 bg-blue-50/50">
              <div className="flex items-center gap-2 mb-3">
                <Plane className="w-4 h-4 text-blue-600 rotate-45" />
                <span className="text-sm font-semibold text-blue-700">Fly Out</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs font-semibold">Date *</Label><Input type="date" value={fifoForm.fly_out_date} onChange={(e) => setFifoForm({ ...fifoForm, fly_out_date: e.target.value })} required data-testid="fifo-fly-out-date" /></div>
                <div><Label className="text-xs font-semibold">Time</Label><Input type="time" value={fifoForm.fly_out_time} onChange={(e) => setFifoForm({ ...fifoForm, fly_out_time: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">Flight #</Label><Input value={fifoForm.fly_out_flight} onChange={(e) => setFifoForm({ ...fifoForm, fly_out_flight: e.target.value.toUpperCase() })} placeholder="QF1235" /></div>
                <div></div>
                <div><Label className="text-xs font-semibold">From</Label><select value={fifoForm.fly_out_from} onChange={(e) => setFifoForm({ ...fifoForm, fly_out_from: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Airport</option>{AIRPORTS.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}</select></div>
                <div><Label className="text-xs font-semibold">To</Label><select value={fifoForm.fly_out_to} onChange={(e) => setFifoForm({ ...fifoForm, fly_out_to: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Airport</option>{AIRPORTS.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}</select></div>
              </div>
            </div>
            {/* Project & Location */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Project</Label><select value={fifoForm.project} onChange={(e) => setFifoForm({ ...fifoForm, project: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"><option value="">Select Project</option>{projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}</select></div>
              <div><Label className="text-xs font-semibold">Location</Label><Input value={fifoForm.location} onChange={(e) => setFifoForm({ ...fifoForm, location: e.target.value })} placeholder="e.g., Karratha" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Notes</Label><Input value={fifoForm.notes} onChange={(e) => setFifoForm({ ...fifoForm, notes: e.target.value })} placeholder="Optional notes" /></div>
          </form>
          <SheetFooter>
            <SheetClose asChild><Button type="button" variant="outline">Cancel</Button></SheetClose>
            <Button type="submit" form="quick-fifo-form" data-testid="save-quick-fifo" className="bg-slate-900 hover:bg-slate-800">{editingFifoId ? 'Update Flight' : 'Add Flight'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
